<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  DEFAULT_MOTION_BOX_OUTBOUND_PRIMARY_ITEMS,
  DEFAULT_MOTION_BOX_OUTBOUND_SECONDARY_ITEMS,
  DEFAULT_MOTION_BOX_OUTBOUND_ZOOM_LEVELS,
  DEFAULT_MOTION_BOX_WAREHOUSE_PRIMARY_ITEMS,
  DEFAULT_MOTION_BOX_WAREHOUSE_SECONDARY_ITEMS,
  DEFAULT_MOTION_BOX_WAREHOUSE_ZOOM_LEVELS,
} from '@/modules/users/constants/user.constants.ts'
import type { Item } from '@/shared/models/shared.model'
import type { UserSetting } from '../models/user.model.ts'

import SegmentedControlButton from '@/shared/components/button/SegmentedControlButton.vue'
import SelectButton from '@/shared/components/button/SelectButton.vue'
import LineDivider from '@/shared/components/divider/LineDivider.vue'
import TabContent from '@/shared/components/tab/TabContent.vue'
import MotionBoxPreview from './MotionBoxPreview.vue'

const model = defineModel<UserSetting>({ required: true })

const isSaving = ref<boolean>(false)
const isLoading = ref<boolean>(false)
const tabIndex = ref<number>(0)

const outboundZoomLevels = ref<Item[]>(DEFAULT_MOTION_BOX_OUTBOUND_ZOOM_LEVELS)
const warehouseZoomLevels = ref<Item[]>(DEFAULT_MOTION_BOX_WAREHOUSE_ZOOM_LEVELS)

const outboundPrimaryOptions = ref<Item[]>(DEFAULT_MOTION_BOX_OUTBOUND_PRIMARY_ITEMS)
const warehousePrimaryOptions = ref<Item[]>(DEFAULT_MOTION_BOX_WAREHOUSE_PRIMARY_ITEMS)

const outboundSecondaryOptions = ref<Item[]>(DEFAULT_MOTION_BOX_OUTBOUND_SECONDARY_ITEMS)
const warehouseSecondaryOptions = ref<Item[]>(DEFAULT_MOTION_BOX_WAREHOUSE_SECONDARY_ITEMS)

const getOutboundPrimary = computed((): Item | undefined =>
  outboundPrimaryOptions.value.find(
    (value) => value.id === model.value.outboundMotionBoxPrimaryTextId,
  ),
)

const getOutboundSecondary = computed((): Item | undefined =>
  outboundSecondaryOptions.value.find(
    (value) => value.id === model.value.outboundMotionBoxSecondaryTextId,
  ),
)

const getWarehousePrimary = computed((): Item | undefined =>
  warehousePrimaryOptions.value.find(
    (value) => value.id === model.value.warehouseMotionBoxPrimaryTextId,
  ),
)

const getWarehouseSecondary = computed((): Item | undefined =>
  warehouseSecondaryOptions.value.find(
    (value) => value.id === model.value.warehouseMotionBoxSecondaryTextId,
  ),
)
</script>

<template>
  <SegmentedControlButton
    v-model="tabIndex"
    :items="[
      { label: $t('glossary.warehouseOperation', 2), disabled: isSaving },
      { label: $t('glossary.outboundShipment', 2), disabled: isSaving },
    ]"
    :loading="isLoading"
  />
  <TabContent v-model="tabIndex" :index="0">
    <div class="flex flex-col gap-4">
      <SelectButton
        v-model="model.warehouseZoomLevel"
        :title="'Zoom level'"
        :label="true"
        :items="warehouseZoomLevels"
        item-key="id"
        item-text="label"
        class="w-full"
      />
      <LineDivider :title="'Motion box display'" />
    </div>
    <div class="flex items-center gap-4">
      <SelectButton
        v-model="model.warehouseMotionBoxPrimaryTextId"
        :title="'Primary text'"
        :label="true"
        :items="warehousePrimaryOptions"
        item-key="id"
        item-text="label"
        class="w-full"
      />
      <SelectButton
        v-model="model.warehouseMotionBoxSecondaryTextId"
        :title="'Secondary text'"
        :label="true"
        :items="warehouseSecondaryOptions"
        item-key="id"
        item-text="label"
        class="w-full"
      />
    </div>
    <div class="flex flex-col pt-2">
      <MotionBoxPreview
        :primary="getWarehousePrimary?.label"
        :secondary="getWarehouseSecondary?.label"
      />
    </div>
  </TabContent>
  <TabContent v-model="tabIndex" :index="1">
    <div class="flex flex-col gap-4">
      <SelectButton
        v-model="model.outboundZoomLevel"
        :title="'Zoom level'"
        :label="true"
        :items="outboundZoomLevels"
        item-key="id"
        item-text="label"
        class="w-full"
      />
      <LineDivider :title="'Motion box display'" />
    </div>
    <div class="flex items-center gap-4">
      <SelectButton
        v-model="model.outboundMotionBoxPrimaryTextId"
        :title="'Primary text'"
        :label="true"
        :items="outboundPrimaryOptions"
        item-key="id"
        item-text="label"
        class="w-full"
      />
      <SelectButton
        v-model="model.outboundMotionBoxSecondaryTextId"
        :title="'Secondary text'"
        :label="true"
        :items="outboundSecondaryOptions"
        item-key="id"
        item-text="label"
        class="w-full"
      />
    </div>
    <div class="pt-2">
      <MotionBoxPreview
        :primary="getOutboundPrimary?.label"
        :secondary="getOutboundSecondary?.label"
      />
    </div>
  </TabContent>
</template>
